import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, Check, Trash2, Clock } from "lucide-react";

export default function Timeline() {
  const { user } = useAuth();
  const birthYear = user?.profile?.birth_year || null;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ year: "", label: "", approx: false });

  const load = async () => { const { data } = await api.get("/timeline"); setEvents(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.label.trim()) return;
    await api.post("/timeline", { year: form.year ? parseInt(form.year, 10) : null, label: form.label, approx: form.approx, confirmed: true });
    setAdding(false); setForm({ year: "", label: "", approx: false }); load();
  };
  const confirm = async (t) => { await api.put(`/timeline/${t.id}`, { ...t, confirmed: true }); load(); };
  const del = async (id) => { await api.delete(`/timeline/${id}`); load(); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  const ageFor = (t) => {
    if (!birthYear || !t.year) return null;
    const a = t.year - birthYear;
    return a >= 0 && a <= 120 ? a : null;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Life Timeline</h1>
          <p className="text-subtle mt-1">The milestones of your story, woven from the dates in your memories. Scroll through the years — approximate dates are welcome.</p>
        </div>
        <button data-testid="add-event-btn" onClick={() => setAdding(true)} aria-label="Add a timeline entry" className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"><Plus size={16} /> Add</button>
      </div>

      {adding && (
        <div className="bg-white rounded-2xl border border-line p-5 space-y-3 animate-fade-up">
          <div className="flex gap-3">
            <input data-testid="event-year-input" type="number" placeholder="Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-28 h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
            <input data-testid="event-label-input" placeholder="What happened? e.g. Married David" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="flex-1 h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <label className="flex items-center gap-2 text-sm text-subtle"><input type="checkbox" checked={form.approx} onChange={(e) => setForm({ ...form, approx: e.target.checked })} /> This date is approximate</label>
          <div className="flex gap-2"><button data-testid="event-save-btn" onClick={add} className="bg-coral text-white px-5 py-2 rounded-full font-semibold text-sm">Save</button><button onClick={() => setAdding(false)} className="text-subtle px-3">Cancel</button></div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-line">
          <Clock className="mx-auto text-sage" size={36} />
          <p className="mt-4 text-subtle">Your timeline builds itself as you share dated memories. You can add events too.</p>
        </div>
      ) : (
        <div data-testid="life-timeline" className="relative pl-10 sm:pl-14">
          {/* the spine */}
          <div className="absolute left-[18px] sm:left-[26px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-teal/60 via-sage/50 to-teal/20 rounded-full" />
          {events.map((t, i) => {
            const age = ageFor(t);
            const yearText = t.year ? (t.approx ? `~${t.year}` : `${t.year}`) : "—";
            return (
              <div key={t.id || i} data-testid={`timeline-event-${i}`} className="relative mb-5 animate-fade-up">
                {/* node on the spine */}
                <div className={`absolute -left-[30px] sm:-left-[38px] top-3 w-4 h-4 rounded-full border-2 shadow-sm ${t.confirmed ? "bg-teal border-teal" : "bg-cream border-sage"}`} />
                <div className="bg-white rounded-2xl border border-line p-4 sm:p-5 flex items-start justify-between gap-3 hover:border-sage/60 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="text-center shrink-0 min-w-[3.5rem]">
                      <div className="font-serif text-2xl text-teal leading-none">{yearText}</div>
                      {age != null && <div className="text-[11px] text-sage mt-1 whitespace-nowrap">{age === 0 ? "born" : `age ${age}`}</div>}
                    </div>
                    <div className="pt-0.5">
                      <p className="text-charcoal leading-snug">{t.label}</p>
                      {!t.confirmed && <span className="mt-1 inline-block text-xs text-sage">suggested from a memory</span>}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1.5 items-end sm:items-center shrink-0">
                    {!t.confirmed && t.id && (
                      <button data-testid={`confirm-event-${i}`} onClick={() => confirm(t)} aria-label={`Add suggested entry "${t.label}" to my timeline`}
                        className="inline-flex items-center gap-1.5 text-teal hover:bg-sagelight rounded-full px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-1">
                        <Check size={16} /> <span className="hidden sm:inline">Add to my timeline</span><span className="sm:hidden">Add</span>
                      </button>
                    )}
                    {t.id && (
                      <button data-testid={`delete-event-${i}`} onClick={() => del(t.id)}
                        aria-label={t.confirmed ? `Delete timeline entry "${t.label}"` : `Dismiss suggested entry "${t.label}"`}
                        className="inline-flex items-center gap-1.5 text-subtle hover:text-destructive rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-destructive/40 focus:ring-offset-1">
                        <Trash2 size={15} /> {t.confirmed ? "Delete" : "Dismiss"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
